import { useState } from 'react';
import type { Collection } from '../../../types/collection';
import { formatLocaleDate } from '../../../utilities/date';
import { JobProgressBar } from '../../../components/JobProgressBar';
import { IoCell } from './IoCell';
import { useRenameCollection } from '../../../hooks/useCollections';
import { notifyError } from '../../../services/notify';

interface Props {
  collection: Collection;
  projectId: string;
  variant: 'processing' | 'processed';
  checked: boolean;
  onToggleCheck: (id: string) => void;
  statusClass: string;
  showResume: boolean;
  isResuming: boolean;
  onResume: () => void;
  canRename?: boolean;
  allNames?: string[];
  onCustodianClick?: (name: string) => void;
}

export function CollectionRow({
  collection: c,
  projectId,
  variant,
  checked,
  onToggleCheck,
  statusClass,
  showResume,
  isResuming,
  onResume,
  canRename,
  allNames,
  onCustodianClick,
}: Props) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const renameCollection = useRenameCollection(projectId);

  function startEditing() {
    setEditValue(c.name);
    setIsEditing(true);
  }

  function commitRename() {
    const newName = editValue.trim();
    if (!newName) {
      notifyError({ title: 'Invalid name', description: 'Collection name cannot be empty.', timeout: 5000 });
      setEditValue(c.name);
      setIsEditing(false);
      return;
    }
    if (newName === c.name) {
      setIsEditing(false);
      return;
    }
    const isDuplicate = (allNames ?? []).some(
      n => n.toLowerCase() === newName.toLowerCase() && n.toLowerCase() !== c.name.toLowerCase()
    );
    if (isDuplicate) {
      notifyError({ title: 'Duplicate name', description: 'A collection with this name already exists in this project.', timeout: 5000 });
      setEditValue(c.name);
      setIsEditing(false);
      return;
    }
    // Server failures surface via the global error handler; invalidation refreshes the row.
    renameCollection.mutate({ collection: c, name: newName });
    setIsEditing(false);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') { e.preventDefault(); commitRename(); }
    if (e.key === 'Escape') { setEditValue(c.name); setIsEditing(false); }
  }

  return (
    <tr className={checked ? 'info' : ''}>
      <td width="15">
        <input
          type="checkbox"
          checked={checked}
          onChange={() => onToggleCheck(c.id)}
        />
      </td>
      <td>
        {isEditing ? (
          <input
            type="text"
            style={{ width: 100 }}
            value={editValue}
            autoFocus
            onChange={e => setEditValue(e.target.value)}
            onBlur={commitRename}
            onKeyDown={handleKeyDown}
            onClick={e => e.stopPropagation()}
          />
        ) : (
          <>
            <a href={`#!/project/${projectId}/collection/${c.id}`}>{c.name}</a>
            {canRename && (
              <i
                className="fas fa-pencil-alt text-muted iio-inline-edit-icon"
                aria-hidden="true"
                onClick={startEditing}
                style={{ marginLeft: 4, cursor: 'pointer' }}
              />
            )}
          </>
        )}
      </td>
      <td>{c.description}</td>
      <td>
        {onCustodianClick ? (
          <a onClick={() => onCustodianClick(c.custodian?.name ?? '')} style={{ cursor: 'pointer' }}>
            <i className="fas fa-user" aria-hidden="true" /> {c.custodian?.name}
          </a>
        ) : (
          <><i className="fas fa-user" aria-hidden="true" /> {c.custodian?.name}</>
        )}
      </td>
      <IoCell
        type={c.originalSource?.type}
        bytes={c.processingSource?.bytes ?? 0}
        files={c.processingSource?.files ?? 0}
      />
      <IoCell
        type={c.processingOutput?.type}
        bytes={c.processingOutput?.bytes ?? 0}
        files={c.processingOutput?.files ?? 0}
      />
      <td>{formatLocaleDate(c.createdAt)}</td>
      <td style={{ width: 100 }}>
        {showResume ? (
          <button
            type="button"
            className="btn btn-xs btn-primary"
            disabled={isResuming}
            onClick={onResume}
          >
            Resume{isResuming && <> <i className="fas fa-circle-notch fa-spin fa-fw" aria-hidden="true" /></>}
          </button>
        ) : (
          <span className={`label ${statusClass}`}>{c.status}</span>
        )}
      </td>
      {variant === 'processing' && (
        <td style={{ width: 100 }}>
          {c.status === 'Processing' && (
            <JobProgressBar percent={c.job?.percentageComplete} />
          )}
        </td>
      )}
    </tr>
  );
}
